/**
 * Testi italiani degli strumenti: connessioni a GitHub, guida al token,
 * collegamenti ai progetti (ADR 0008 §5).
 *
 * I nomi dei campi di GitHub («Resource owner», «Generate token»…) restano in
 * inglese dentro le frasi: sono le parole che l'utente trova sulla pagina di
 * GitHub, e tradurle gliele farebbe cercare invano.
 */

/** Testi degli strumenti. */
export const tools = {
  /** Pagina «Strumenti». */
  page: {
    /** Titolo. */
    title: 'Strumenti',
    /** A che cosa serve. */
    description:
      'I servizi esterni che hai collegato. Un progetto li usa solo dopo che li importi nella sua scheda Strumenti.',
    /** Titolo della sezione delle connessioni. */
    connections: 'Connessioni',
    /** Che cosa fa una connessione. */
    connectionsIntro:
      'Gli agenti non vedono mai token e credenziali: li usa un intermediario che applica le regole di ogni progetto.',
  },

  /** Pagina «Piattaforma» dell'amministrazione (ADR 0030 §2.3). */
  platform: {
    /** Titolo. */
    title: 'Piattaforma',
    /** A che cosa serve. */
    description: 'Gli account GitHub e Google di institor, usati dai progetti che non ne collegano uno proprio.',
  },

  /** Metodi di collegamento. */
  method: {
    /** Token a grana fine. */
    fine_grained_pat: 'Token a grana fine',
    /** Token classico. */
    classic_pat: 'Token classico',
    /** Flusso per dispositivo. */
    device_flow: 'Accesso dal browser',
    /** JSON di `gcloud auth application-default login`. */
    google_user_credentials: 'Credenziali del tuo account',
    /** Chiave JSON di un service account. */
    google_service_account: 'Service account',
  },

  /** Nomi dei fornitori. */
  provider: {
    /** GitHub. */
    github: 'GitHub',
    /** Google. */
    google: 'Google',
  },

  /** Stato di una connessione. */
  status: {
    /** Valida. */
    active: 'Attiva',
    /** Rifiutata da GitHub. */
    invalid: 'Non valida',
    /** Scaduta. */
    expired: 'Scaduta',
  },

  /** Accesso di un progetto a un repository. */
  access: {
    /** Lettura. */
    read: 'Lettura',
    /** Lettura e scrittura. */
    write: 'Lettura e scrittura',
  },

  /** Rifiuti del fornitore. */
  errors: {
    /** Token rifiutato. */
    tokenInvalid: 'GitHub rifiuta il token. Controlla di averlo copiato per intero e che non sia scaduto.',
    /** Token che legge ma non fa push. */
    cannotPush: 'GitHub non lascia fare push a questo token sul repository. Nelle impostazioni del token su GitHub scegli il repository e dai «Contents: Read and write», oppure collega il progetto in sola lettura.',
    /** GitHub non raggiungibile. */
    unreachable: 'GitHub non risponde. Riprova fra qualche minuto.',
    /** Connessione con la stessa etichetta per lo stesso account. */
    connectionExists: 'Esiste già una connessione con questo nome per questo account GitHub. Scegli un altro nome.',
    /** Token di un altro account nella rotazione. */
    accountMismatch: 'Il token appartiene a un altro account GitHub. Per un altro account crea una connessione nuova.',
    /** Collegamento già presente. */
    bindingExists: 'Il progetto ha già questo collegamento.',
    /** Flusso per dispositivo non attivo. */
    deviceFlowUnavailable: 'Il collegamento con codice non è attivo su questa piattaforma. Usa un token.',
  },

  /** Cassetto «Collega GitHub». */
  connect: {
    /** Pulsante che apre il cassetto. */
    open: 'Collega GitHub',
    /** Titolo del cassetto. */
    title: 'Collega GitHub',
    /** Pulsante di invio. */
    submit: 'Collega',
    /** Etichetta del metodo. */
    method: 'Metodo',
    /** Opzione consigliata. */
    recommended: '{{label}} (consigliato)',
    /** Etichetta del nome. */
    label: 'Nome della connessione',
    /** Aiuto sotto il nome. */
    labelHelp: 'Ti serve a riconoscerla, per esempio «GitHub lavoro».',
    /** Etichetta del token. */
    token: 'Token',
    /** Token mancante. */
    tokenMissing: 'Incolla il token prima di collegare.',
    /** Esito riuscito. */
    done: 'GitHub collegato.',
    /** Esito fallito. */
    failed: 'Collegamento non riuscito. Riprova.',
  },

  /** Guida al token. */
  guide: {
    /** Scadenza consigliata. */
    expiration: 'In «Expiration» scegli 90 giorni: quando scade, qui la connessione diventa «Scaduta» e la rinnovi.',
    /** Che cosa succede dopo. */
    after:
      'Dopo che premi «Collega», institor verifica il token con GitHub, lo cifra e non lo mostra più. Nei progetti lo limiti a un repository.',
    /** Token a grana fine. */
    fine: {
      /** Primo passo. */
      open: 'Apri la pagina dei nuovi token a grana fine:',
      /** Testo del collegamento. */
      openLink: 'nuovo token a grana fine',
      /** Nome. */
      name: 'In «Token name» scrivi un nome che riconosci, per esempio «institor».',
      /** Proprietario. */
      owner: "In «Resource owner» scegli il tuo account o l'organizzazione che possiede i repository.",
      /** Repository. */
      repositories: 'In «Repository access» scegli «Only select repositories» e indica quelli che gli agenti useranno.',
      /** Permessi. */
      permissions: 'In «Repository permissions» imposta:',
      /** Lettura e scrittura. */
      readWrite: '«Read and write»',
      /** Sola lettura. */
      readOnly: '«Read-only» (GitHub lo aggiunge da solo)',
      /** Issue facoltative. */
      issuesOptional: '«Read and write», solo se vuoi che gli agenti aprano issue',
      /** Ultimo passo. */
      generate: 'Premi «Generate token», copia il valore (inizia con github_pat_) e incollalo qui sotto.',
    },
    /** Token classico. */
    classic: {
      /** Primo passo. */
      open: 'Apri la pagina dei token classici, con lo scope repo già scelto:',
      /** Testo del collegamento. */
      openLink: 'nuovo token classico',
      /** Scope. */
      scope: 'Lascia selezionato solo «repo».',
      /** Ultimo passo. */
      generate: 'Premi «Generate token», copia il valore (inizia con ghp_) e incollalo qui sotto.',
      /** Avvertenza. */
      warning:
        'Un token classico vede tutti i repository del tuo account. Il token a grana fine è più sicuro: preferiscilo se puoi.',
    },
  },

  /** Flusso per dispositivo. */
  device: {
    /** Come funziona. */
    intro: 'Niente token da copiare: ricevi un codice, lo inserisci su GitHub e confermi.',
    /** Pulsante di avvio. */
    start: 'Ricevi il codice',
    /** Pulsante per ricominciare. */
    restart: 'Chiedi un nuovo codice',
    /** Istruzione. */
    enterCode: 'Inserisci questo codice su GitHub:',
    /** Collegamento. */
    openGithub: 'Apri GitHub',
    /** Attesa. */
    waiting: 'In attesa della conferma su GitHub…',
    /** Codice scaduto. */
    expired: 'Il codice è scaduto. Chiedine uno nuovo.',
    /** Accesso negato. */
    denied: 'Su GitHub hai negato l’accesso. Chiedi un nuovo codice per riprovare.',
    /** Avvio non riuscito. */
    startFailed: 'Avvio non riuscito. Riprova.',
    /** Interrogazione non riuscita. */
    pollFailed: 'Verifica non riuscita. Riprovo fra poco.',
  },

  /** Tabella delle connessioni. */
  connections: {
    /** Nessuna connessione. */
    empty: 'Nessuna connessione. Collega GitHub o Google.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento delle connessioni non riuscito. Riprova.',
    /** Operazione non riuscita. */
    actionFailed: 'Operazione non riuscita. Riprova.',
    /** Token senza scadenza. */
    noExpiry: 'Senza scadenza',
    /** Colonne. */
    columns: {
      /** Nome. */
      label: 'Nome',
      /** Fornitore. */
      provider: 'Fornitore',
      /** Account del fornitore. */
      account: 'Account',
      /** Metodo. */
      method: 'Metodo',
      /** Scadenza. */
      expires: 'Scade il',
      /** Ultima verifica. */
      checked: 'Verificata il',
      /** Progetti collegati. */
      projects: 'Progetti',
    },
    /** Pulsante di verifica. */
    verify: 'Verifica',
    /** Nome accessibile della verifica. */
    verifyNamed: 'Verifica {{name}}',
    /** Esito della verifica. */
    verified: 'Verifica conclusa.',
    /** Pulsante di modifica. */
    edit: 'Modifica',
    /** Nome accessibile della modifica. */
    editNamed: 'Modifica {{name}}',
    /** Pagina di gestione di una connessione Google. */
    manage: 'Gestisci',
    /** Nome accessibile della gestione. */
    manageNamed: 'Gestisci {{name}}',
    /** Cancellazione. */
    delete: {
      /** Etichetta del pulsante. */
      label: 'Elimina',
      /** Nome accessibile. */
      accessibleName: 'Elimina {{name}}',
      /** Domanda della conferma. */
      question: 'Eliminare {{name}}?',
      /** Senza progetti collegati. */
      consequence: 'La credenziale viene cancellata. Non si annulla.',
      /** Con progetti collegati. */
      consequenceBound_one: 'La credenziale viene cancellata e {{count}} progetto perde il collegamento. Non si annulla.',
      /** Con progetti collegati. */
      consequenceBound_other:
        'La credenziale viene cancellata e {{count}} progetti perdono il collegamento. Non si annulla.',
      /** Esito riuscito. */
      done: 'Connessione eliminata.',
    },
  },

  /** Cassetto di modifica. */
  edit: {
    /** Titolo. */
    title: 'Modifica {{name}}',
    /** Pulsante di invio. */
    submit: 'Salva',
    /** Etichetta del nuovo token. */
    token: 'Nuovo token',
    /** Aiuto. */
    tokenHelp: 'Lascia vuoto per tenere quello attuale. Il token attuale non si può rileggere.',
    /** Esito: solo nome. */
    done: 'Connessione aggiornata.',
    /** Esito: token sostituito. */
    rotated: 'Token sostituito.',
    /** Esito fallito. */
    failed: 'Salvataggio non riuscito. Riprova.',
  },

  /** Selettore dei repository. */
  repositories: {
    /** Testo di riempimento. */
    placeholder: 'Cerca un repository',
    /** Nessun risultato. */
    none: 'Nessun repository trovato',
    /** Repository privato. */
    private: 'Privato',
    /** Senza permesso di scrittura. */
    readOnly: 'Sola lettura',
    /** Pagina successiva. */
    more: 'Carica altri',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei repository non riuscito. Riprova.',
    /** Opzione per un repository che non esiste ancora. */
    create: 'Crea il repository {{name}}',
    /** Repository da creare, accanto al nome scelto. */
    new: 'Nuovo',
  },

  /** Visibilità di un repository che creano gli agenti. */
  visibility: {
    /** Privato. */
    private: 'Privato',
    /** Pubblico. */
    public: 'Pubblico',
  },

  /** Cassetto «Importa dai miei strumenti». */
  import: {
    /** Titolo del cassetto e pulsante che lo apre. */
    title: 'Importa dai miei strumenti',
    /** Pulsante di invio. */
    submit: 'Importa',
    /** Che cosa succede. */
    intro:
      'Gli agenti del progetto potranno usare solo il repository che scegli qui, anche se il token ne vede altri.',
    /** Etichetta della connessione. */
    connection: 'Connessione',
    /** Testo di riempimento. */
    connectionPlaceholder: 'Scegli una connessione',
    /** Nessuna connessione. */
    noConnections: 'Nessuna connessione: collegala dalla pagina Strumenti',
    /** Gruppo delle mie. */
    groupMine: 'Le mie',
    /** Gruppo del progetto. */
    groupProject: 'Del progetto',
    /** Connessione mancante. */
    connectionMissing: 'Scegli una connessione.',
    /** Etichetta del repository. */
    repository: 'Repository',
    /** Aiuto sotto il repository. */
    repositoryHelp:
      "L'unico repository che gli agenti del progetto raggiungeranno. Se non esiste ancora, scrivi owner/nome.",
    /** Repository mancante. */
    repositoryMissing: 'Scegli il repository.',
    /** Etichetta dell'accesso. */
    access: 'Accesso degli agenti',
    /** Repository senza scrittura. */
    readOnlyRepository: 'Il tuo account non può scrivere su questo repository: resta la sola lettura.',
    /** Repository da creare. */
    createHelp: 'Il repository non esiste ancora: lo creano gli agenti prima del primo push. Serve la scrittura.',
    /** Etichetta della visibilità del repository nuovo. */
    visibility: 'Visibilità del repository',
    /** Esito riuscito. */
    done: 'Strumento importato nel progetto.',
    /** Esito fallito. */
    failed: 'Importazione non riuscita. Riprova.',
    /** Permesso mancante. */
    forbidden: 'Puoi importare solo le tue connessioni, e devi amministrare il progetto.',
    /** Collegamento già presente. */
    duplicate: 'Questo repository è già collegato al progetto con la stessa connessione.',
  },

  /** Impostazioni avanzate di un collegamento. */
  settings: {
    /** Titolo della sezione richiudibile. */
    title: 'Impostazioni avanzate (facoltative)',
    /** Push sul ramo predefinito. */
    allow_default_branch_push: 'Push sul ramo predefinito',
    /** Aiuto. */
    allow_default_branch_pushHelp: 'Spento, gli agenti lavorano su rami propri e propongono le modifiche.',
    /** Prefisso dei rami. */
    branch_prefix: 'Prefisso dei rami',
    /** Aiuto. */
    branch_prefixHelp: 'Gli agenti possono fare push solo su rami con questo prefisso. Vuoto, nessun vincolo.',
    /** Pull request. */
    allow_pull_requests: 'Pull request',
    /** Aiuto. */
    allow_pull_requestsHelp: 'Gli agenti possono aprire e aggiornare pull request.',
    /** Issue. */
    allow_issues: 'Issue e commenti',
    /** Aiuto. */
    allow_issuesHelp: 'Gli agenti possono aprire issue e commentare.',
    /** Nome dell'autore dei commit. */
    commit_name: 'Nome per i commit',
    /** Email dell'autore dei commit. */
    commit_email: 'Email per i commit',
    /** Aiuto. */
    commit_emailHelp: 'Vuoti, vale quello del profilo degli agenti.',
  },

  /** Scheda «Strumenti» del progetto. */
  bindings: {
    /** Titolo della sezione. */
    title: 'Strumenti del progetto',
    /** Che cosa sono. */
    intro:
      'Gli agenti del progetto raggiungono GitHub e Google solo attraverso questi collegamenti, con l’accesso e le regole indicati.',
    /** Nessun collegamento. */
    empty: 'Nessuno strumento collegato.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei collegamenti non riuscito. Riprova.',
    /** Permesso mancante. */
    forbidden: 'Per vedere e modificare gli strumenti devi amministrare questo progetto.',
    /** Tutti i repository. */
    allRepositories: 'Tutti quelli del token',
    /** Connessione dell'utente. */
    ownerUser: 'Personale',
    /** Connessione del progetto. */
    ownerProject: 'Del progetto',
    /** Account di institor (ADR 0014). */
    ownerPlatform: 'Di institor',
    /** Pulsante del token di progetto. */
    projectToken: 'Collega un token solo per questo progetto',
    /** Titolo del cassetto del token di progetto. */
    projectTokenTitle: 'Token solo per questo progetto',
    /** Titolo della sezione delle connessioni del progetto. */
    projectConnections: 'Token del progetto',
    /** Nessuna connessione del progetto. */
    noProjectConnections: 'Nessun token collegato solo a questo progetto.',
    /** Colonne. */
    columns: {
      /** Repository o progetto Google Cloud. */
      repository: 'Repository o progetto',
      /** Connessione. */
      connection: 'Connessione',
      /** Accesso. */
      access: 'Accesso',
      /** Regole. */
      rules: 'Regole',
    },
    /** Riassunto delle regole. */
    rules: {
      /** Sola lettura. */
      readOnly: 'Nessuna scrittura',
      /** Nessun vincolo sui rami. */
      anyBranch: 'Qualunque ramo',
      /** Prefisso dei rami. */
      prefix: 'Rami {{prefix}}*',
      /** Push sul ramo predefinito. */
      defaultPush: 'push sul ramo predefinito',
      /** Pull request. */
      pulls: 'pull request',
      /** Issue. */
      issues: 'issue',
    },
    /** Nome accessibile della modifica. */
    editNamed: 'Modifica il collegamento di {{name}}',
    /** Titolo del cassetto di modifica. */
    editTitle: 'Collegamento di {{name}}',
    /** Spiegazione nel cassetto. */
    editIntro: 'Connessione: {{connection}}. Per un altro repository togli questo collegamento e importane uno nuovo.',
    /** Pulsante di invio. */
    editSubmit: 'Salva',
    /** Esito. */
    edited: 'Collegamento aggiornato.',
    /** Esito fallito. */
    editFailed: 'Salvataggio non riuscito. Riprova.',
    /** Rimozione fallita. */
    removeFailed: 'Rimozione non riuscita. Riprova.',
    /** Rimozione. */
    remove: {
      /** Etichetta. */
      label: 'Togli',
      /** Nome accessibile. */
      accessibleName: 'Togli il collegamento di {{name}}',
      /** Domanda. */
      question: 'Togliere il collegamento di {{name}}?',
      /** Conseguenza. */
      consequence: 'Gli agenti del progetto perdono questo accesso dal prossimo avvio del set.',
      /** Esito. */
      done: 'Collegamento tolto.',
    },
  },
};

/** Forma dei testi degli strumenti. */
export type Tools = typeof tools;
